import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Club, clubService } from '../services/clubService';
import { clearAuthSession, getAuthItem } from '../services/authStorage';
import '../styles/WalletClubsPage.css';

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number.isFinite(value) ? value : 0);

const LOCAL_APP_ASSET_PREFIX = '/assets/avatars/';
const ABSOLUTE_URL_PATTERN = /^(https?:|capacitor:|data:|blob:)/i;

const resolveAvatarUrl = (value?: string | null): string => {
  const normalized = (value || '').trim();

  if (!normalized) {
    return '';
  }

  if (ABSOLUTE_URL_PATTERN.test(normalized)) {
    return normalized;
  }

  if (normalized.startsWith(LOCAL_APP_ASSET_PREFIX)) {
    return normalized;
  }

  return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

const ClubsIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M4.5 17.5c0-2.2 1.8-4 4-4h7c2.2 0 4 1.8 4 4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <circle cx="8.5" cy="8" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
    <circle cx="15.5" cy="8" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);

const WalletIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M4.5 7.5h13a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path d="M15.5 11.5h3.5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M15.5 14.5h1.8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const ProfileIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm0 10c-4.2 0-7 2.1-7 5v1h14v-1c0-2.9-2.8-5-7-5Z"
      fill="currentColor"
    />
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M10 7V6a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2v-1"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M3.5 12h10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="m7.5 8 4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WalletClubsPage: React.FC = () => {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const userName = getAuthItem('userName') || 'Jogador';
  const userAvatar = resolveAvatarUrl(getAuthItem('userAvatar'));
  const userInitials = useMemo(() => {
    const parts = userName.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || 'J';
    const second = parts.length > 1 ? parts[1]?.[0] : '';
    return `${first}${second}`.toUpperCase();
  }, [userName]);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const payload = await clubService.listClubs(1, 24);
        if (!isMounted) return;
        setClubs(Array.isArray(payload.data) ? payload.data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setError(err?.message || 'Nao foi possivel carregar os clubs do jogador.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredClubs = useMemo(() => {
    const normalizedQuery = searchTerm.trim().toLowerCase();

    if (!normalizedQuery) {
      return clubs;
    }

    return clubs.filter((club) => {
      const clubName = club.name?.toLowerCase() || '';
      const clubCode = club.code?.toLowerCase() || '';
      return clubName.includes(normalizedQuery) || clubCode.includes(normalizedQuery);
    });
  }, [clubs, searchTerm]);

  const visibleWallet = useMemo(
    () => filteredClubs.reduce((sum, club) => sum + Number(club.current_user_wallet_balance || 0), 0),
    [filteredClubs]
  );

  const handleLogout = () => {
    clearAuthSession();
    navigate('/login', { replace: true });
  };

  return (
    <div className="wallet-clubs-page">
      <div className="wallet-clubs-page__texture" />
      <header className="wallet-clubs-topbar">
        <div className="wallet-clubs-topbar__title">
          <span className="wallet-clubs-topbar__eyebrow">
            <WalletIcon />
            Clubs
          </span>
          <h1>Carteira</h1>
        </div>
        <div className="wallet-clubs-topbar__actions">
          <div className="wallet-clubs-topbar__profile" aria-label={userName}>
            <div className="wallet-clubs-topbar__avatar">
              {userAvatar ? <img src={userAvatar} alt={userName} /> : <span>{userInitials}</span>}
            </div>
            <strong>{userName}</strong>
          </div>
          <button type="button" onClick={handleLogout}>
            <ProfileIcon />
            <LogoutIcon />
            Sair
          </button>
        </div>
      </header>

      <section className="wallet-clubs-summary">
        <article className="wallet-clubs-summary__card">
          <span>
            <ClubsIcon />
            Clubs
          </span>
          <strong>{filteredClubs.length}</strong>
        </article>
        <article className="wallet-clubs-summary__card wallet-clubs-summary__card--accent">
          <span>
            <WalletIcon />
            Total
          </span>
          <strong>{formatCurrency(visibleWallet)}</strong>
        </article>
      </section>

      <section className="wallet-clubs-search">
        <label className="wallet-clubs-search__field" htmlFor="wallet-clubs-search-input">
          <span>Buscar club</span>
          <div className="wallet-clubs-search__control">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
              <path d="m16 16 4 4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
            <input
              id="wallet-clubs-search-input"
              type="search"
              placeholder="Digite o nome ou codigo"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {searchTerm ? (
              <button type="button" onClick={() => setSearchTerm('')} aria-label="Limpar busca">
                ×
              </button>
            ) : null}
          </div>
        </label>
      </section>

      <section className="wallet-clubs-list">
        {loading && <div className="wallet-clubs-empty">Carregando clubs...</div>}
        {!loading && error && <div className="wallet-clubs-empty wallet-clubs-empty--error">{error}</div>}
        {!loading && !error && clubs.length === 0 && (
          <div className="wallet-clubs-empty">Nenhum club disponivel para este jogador.</div>
        )}
        {!loading && !error && clubs.length > 0 && filteredClubs.length === 0 && (
          <div className="wallet-clubs-empty">Nenhum club encontrado para essa busca.</div>
        )}

        {!loading && !error && filteredClubs.length > 0 && (
          <div className="wallet-clubs-grid">
            {filteredClubs.map((club) => (
              <Link
                key={club.id}
                to={`/clubs/${club.id}/wallet`}
                className="wallet-clubs-card"
                aria-label={`Abrir carteira do ${club.name}`}
              >
                <div className="wallet-clubs-card__head">
                  <div className="wallet-clubs-card__headline">
                    <span className="wallet-clubs-card__icon">
                      <ClubsIcon />
                    </span>
                    <strong>{club.name}</strong>
                  </div>
                </div>
                <p>
                  {club.is_public ? 'Club publico' : 'Club privado'} • {club.total_members} membros •{' '}
                  {club.total_tables} mesas
                </p>
                <div className="wallet-clubs-card__balance">
                  <span>
                    <WalletIcon />
                    Carteira
                  </span>
                  <span className="wallet-clubs-card__divider" aria-hidden="true" />
                  <strong>{formatCurrency(Number(club.current_user_wallet_balance || 0))}</strong>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default WalletClubsPage;
